import axios, { AxiosInstance, AxiosError, AxiosRequestConfig } from 'axios';
import { setupCache } from 'axios-cache-interceptor';

// API Configuration
const API_CONFIG = {
  timeout: 30000,
  maxRetries: 3,
  retryDelay: 1000,
  cacheTime: 5 * 60 * 1000, // 5 minutes
  retryableStatuses: [408, 429, 500, 502, 503, 504],
};

// Error types
export class ApiError extends Error {
  constructor(
    message: string,
    public status?: number,
    public code?: string,
    public retryCount?: number
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface RetryConfig extends AxiosRequestConfig {
  _retryCount?: number;
  _isRetry?: boolean;
}

// Create API client factory
export function createApiClient(baseURL: string, apiKey?: string): AxiosInstance {
  // Always rewrite ClickUp API URLs to use our proxy
  const finalBaseURL = baseURL.includes('api.clickup.com')
    ? '/api/clickup'
    : baseURL;

  const client = setupCache(
    axios.create({
      baseURL: finalBaseURL,
      timeout: API_CONFIG.timeout,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...(apiKey && { Authorization: apiKey }),
      },
    })
  );

  // Request interceptor
  client.interceptors.request.use(
    (config) => {
      const retryConfig = config as RetryConfig;
      
      if (!retryConfig._isRetry) {
        retryConfig._retryCount = 0;
      }

      return retryConfig;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor with retry logic
  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const config = error.config as RetryConfig;
      
      if (!config) {
        return Promise.reject(new ApiError('Request configuration not available', 500));
      }

      // Check if we should retry the request
      const shouldRetry = (error: AxiosError): boolean => {
        if (config._retryCount >= API_CONFIG.maxRetries) return false;
        
        // Don't retry on CORS errors
        if (error.message.includes('CORS')) return false;
        
        if (error.response?.status === 0 || error.message.includes('Network Error')) {
          return true;
        }
        
        if (error.response?.status && error.response.status < 500) {
          return API_CONFIG.retryableStatuses.includes(error.response.status);
        }

        return !error.response || API_CONFIG.retryableStatuses.includes(error.response.status);
      };

      if (shouldRetry(error)) {
        config._retryCount = (config._retryCount || 0) + 1;
        config._isRetry = true;

        const backoffDelay = Math.min(
          API_CONFIG.retryDelay * Math.pow(2, config._retryCount - 1),
          10000
        );

        await new Promise(resolve => setTimeout(resolve, backoffDelay));

        return client(config);
      }

      // Format error message
      let errorMessage = 'An error occurred while processing your request';
      let errorStatus = error.response?.status || 500;

      if (error.response) {
        if (errorStatus === 401) {
          errorMessage = 'Unauthorized access. Please check your API key.';
        } else if (errorStatus === 403) {
          errorMessage = 'Access forbidden. You don\'t have permission to access this resource.';
        } else if (errorStatus === 404) {
          errorMessage = 'The requested resource was not found.';
        } else if (errorStatus === 429) {
          errorMessage = 'Too many requests. Please try again later.';
        } else if (errorStatus >= 500) {
          errorMessage = 'A server error occurred. Please try again later.';
        }
      } else if (error.request) {
        if (error.code === 'ECONNABORTED') {
          errorMessage = 'The request timed out. Please try again.';
          errorStatus = 408;
        } else {
          errorMessage = 'No response received from server. Please check your connection.';
          errorStatus = 503;
        }
      }

      const apiError = new ApiError(
        errorMessage,
        errorStatus,
        error.code,
        config._retryCount
      );

      if (config._retryCount > 0) {
        apiError.message += ` (Failed after ${config._retryCount} retries)`;
      }

      return Promise.reject(apiError);
    }
  );

  return client;
}